import axios from 'axios';
import useAuthStore from '../store/authStore';

const API_BASE_URL = import.meta.env.VITE_API_URL || 'http://localhost:8000';

// Create axios instance
const apiClient = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json'
  }
});

// Request interceptor - add auth token
apiClient.interceptors.request.use(
  (config) => {
    const token = useAuthStore.getState().token;
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    
    // Add tenant_id to headers if present
    const tenantId = useAuthStore.getState().tenantId;
    if (tenantId) {
      config.headers['X-Tenant-ID'] = tenantId;
    }
    
    return config;
  },
  (error) => Promise.reject(error)
);

// Response interceptor - handle 401 errors
apiClient.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      // Token expired or invalid — clear session and return to root.
      // FIX #6: This app has no React Router. /login is not a real route.
      // Redirect to '/' which re-renders the Login component via main.jsx state logic.
      useAuthStore.getState().logout();
      window.location.href = '/';
    }
    return Promise.reject(error);
  }
);

/**
 * ============================================
 * SUPER ADMIN APIs - God Mode
 * ============================================
 */

// Tenant Management
export const getAllTenants = () => apiClient.get('/api/admin/tenants');
export const getTenantDetails = (tenantId) => apiClient.get(`/api/admin/tenants/${tenantId}`);
export const suspendTenant = (tenantId) => apiClient.post(`/api/admin/tenants/${tenantId}/suspend`);
export const activateTenant = (tenantId) => apiClient.post(`/api/admin/tenants/${tenantId}/activate`);
export const updateTenantPlan = (tenantId, plan) => apiClient.put(`/api/admin/tenants/${tenantId}/plan`, { plan });
export const impersonateTenant = (tenantId) => apiClient.post(`/api/admin/tenants/${tenantId}/impersonate`);

// AI & Resource Monitoring
export const getTokenUsage = (tenantId, period = '30d') => apiClient.get(`/api/admin/monitoring/tokens/${tenantId}?period=${period}`);
export const getSystemErrors = (limit = 100) => apiClient.get(`/api/admin/monitoring/errors?limit=${limit}`);
export const getServerStatus = () => apiClient.get('/api/admin/monitoring/servers');

// Billing & Finance
export const getMRR = () => apiClient.get('/api/admin/billing/mrr');
export const getInvoices = (page = 1) => apiClient.get(`/api/admin/billing/invoices?page=${page}`);
export const generateLicenseKey = (tenantId, plan) => apiClient.post('/api/admin/billing/license', { tenant_id: tenantId, plan });

/**
 * ============================================
 * TENANT ADMIN APIs - Agency Mode
 * ============================================
 */

// CRM & Leads
export const getLeads = (tenantId, status = null) => apiClient.get(`/api/tenants/${tenantId}/leads`, { params: { status } });
export const getLeadDetails = (tenantId, leadId) => apiClient.get(`/api/tenants/${tenantId}/leads/${leadId}`);
export const updateLeadStatus = (tenantId, leadId, status) => apiClient.patch(`/api/tenants/${tenantId}/leads/${leadId}`, { status });
export const assignLeadToAgent = (tenantId, leadId, agentId) => apiClient.post(`/api/tenants/${tenantId}/leads/${leadId}/assign`, { agent_id: agentId });
export const addLeadNote = (tenantId, leadId, note) => apiClient.post(`/api/tenants/${tenantId}/leads/${leadId}/notes`, { content: note });

// Live Chat & Takeover
export const getActiveChats = () => apiClient.get('/api/v1/chats/active');
export const getChatHistory = (leadId) => apiClient.get(`/api/v1/chats/${leadId}/history`);
export const takeoverChat = (leadId) => apiClient.post(`/api/v1/chats/${leadId}/takeover`);
export const releaseChat = (leadId) => apiClient.post(`/api/v1/chats/${leadId}/release`);
export const sendManualMessage = (leadId, message) => apiClient.post(`/api/v1/chats/${leadId}/send`, { message });

// Bot Configuration
export const getBotSettings = () => apiClient.get('/api/v1/settings/bot');
export const updateBotSettings = (settings) => apiClient.put('/api/v1/settings/bot', settings);
export const uploadInventory = (tenantId, file) => {
  const formData = new FormData();
  formData.append('file', file);
  return apiClient.post(`/api/tenants/${tenantId}/properties/upload-pdf`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  });
};
export const getInventory = (tenantId) => apiClient.get(`/api/tenants/${tenantId}/properties`);
export const deleteProperty = (tenantId, propertyId) => apiClient.delete(`/api/tenants/${tenantId}/properties/${propertyId}`);

// Analytics
export const getSalesFunnel = (period = '30d') => apiClient.get(`/api/v1/analytics/funnel?period=${period}`);
export const getAgentPerformance = (period = '30d') => apiClient.get(`/api/v1/analytics/agents?period=${period}`);
export const exportLeads = (filters) => apiClient.get('/api/v1/analytics/export', { params: filters, responseType: 'blob' });

/**
 * ============================================
 * AGENT APIs - Sales Mode
 * ============================================
 */

// My Leads (filtered by agent)
export const getMyLeads = () => apiClient.get('/api/v1/agent/leads');
export const getMyTasks = () => apiClient.get('/api/v1/agent/tasks');
export const createTask = (leadId, task) => apiClient.post('/api/v1/agent/tasks', { lead_id: leadId, ...task });
export const markTaskDone = (taskId) => apiClient.put(`/api/v1/agent/tasks/${taskId}/done`);

// Quick Actions
export const sendWhatsApp = (leadId, message) => apiClient.post(`/api/v1/agent/leads/${leadId}/whatsapp`, { message });
export const sendPDF = (leadId, pdfType) => apiClient.post(`/api/v1/agent/leads/${leadId}/pdf`, { type: pdfType });
export const addQuickNote = (leadId, note) => apiClient.post(`/api/v1/agent/leads/${leadId}/note`, { content: note });

/**
 * ============================================
 * SHARED APIs
 * ============================================
 */

// Authentication
export const login = (email, password) => apiClient.post('/api/v1/auth/login', { email, password });
export const logout = () => apiClient.post('/api/v1/auth/logout');
export const getProfile = () => apiClient.get('/api/v1/auth/profile');

// Notifications
export const getNotifications = () => apiClient.get('/api/v1/notifications');
export const markNotificationRead = (notificationId) => apiClient.put(`/api/v1/notifications/${notificationId}/read`);

export default apiClient;
